import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { BarsResponse, RangeKey } from "@chartandgo/shared";
import { isIntradayRange } from "@chartandgo/shared";
import { fetchBars } from "../api/market.js";

/**
 * Fetch historical bars for a single card. Intraday ranges refresh more often
 * than long-horizon ranges, mirroring how frequently the data actually changes.
 */
export function useBars(symbol: string, range: RangeKey) {
  const query = useInfiniteQuery({
    queryKey: ["bars", symbol, range],
    queryFn: ({ pageParam }) =>
      fetchBars(symbol, range, pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage: BarsResponse) =>
      lastPage.nextBefore ?? undefined,
    staleTime: isIntradayRange(range) ? 30_000 : 5 * 60_000,
    refetchInterval: isIntradayRange(range) ? 60_000 : false,
    retry: 1,
  });

  const data = useMemo<BarsResponse | undefined>(() => {
    if (!query.data) return undefined;
    const latest = query.data.pages[0] as BarsResponse | undefined;
    const oldest = query.data.pages.at(-1) as BarsResponse | undefined;
    if (!latest || !oldest) return undefined;

    const barsByTime = new Map(
      query.data.pages.flatMap((page) =>
        (page as BarsResponse).bars.map((bar) => [bar.time, bar] as const),
      ),
    );
    return {
      ...latest,
      bars: [...barsByTime.values()].sort((a, b) => a.time - b.time),
      nextBefore: oldest.nextBefore,
    };
  }, [query.data]);

  // Keep the originally selected window separate from history pages. The chart
  // may load older pages as the user scrolls, but the range return must not
  // silently expand with them.
  const selectedRangeBars =
    (query.data?.pages[0] as BarsResponse | undefined)?.bars ?? [];

  return { ...query, data, selectedRangeBars };
}
